import {makeAutoObservable, observable} from 'mobx';

import {UndoInfo, User, UserDetailed} from '@/domain';
import {ONE_SECOND_MS} from '@/shared/consts/time';
import {withStableAvatars} from '@/shared/lib/stabilizeAvatars';

import {PendingReaction} from './types';

class DiscoveryStore {
  users: UserDetailed[] = [];
  private _pendingReactions = new Set<PendingReaction>();
  private _recentUndoUserId: number | undefined;
  private _clearRecentUndoUserIdTimeout:
    | ReturnType<typeof setTimeout>
    | undefined;

  fetchedAllUsers = false;
  undoInfo: UndoInfo | null = null;
  filtersExtended: boolean = false;
  restoredFromCache = false;

  constructor() {
    makeAutoObservable<
      this,
      | '_pendingReactions'
      | '_recentUndoUserId'
      | '_clearRecentUndoUserIdTimeout'
    >(this, {
      _pendingReactions: observable.ref,
      _recentUndoUserId: observable.ref,
      _clearRecentUndoUserIdTimeout: observable.ref,
    });
  }

  cleanUp() {
    this.users = [];
    this.fetchedAllUsers = false;
    this.filtersExtended = false;
  }

  get pendingReactions() {
    return this._pendingReactions;
  }

  addPendingReaction(reaction: PendingReaction) {
    this._pendingReactions.add(reaction);
  }

  deletePendingReaction(reaction: PendingReaction) {
    this._pendingReactions.delete(reaction);
  }

  isRecentUndoUserId(userId: number) {
    return this._recentUndoUserId === userId;
  }

  setRecentUndoUserId(userId: number) {
    this._recentUndoUserId = userId;

    if (this._clearRecentUndoUserIdTimeout) {
      clearTimeout(this._clearRecentUndoUserIdTimeout);
    }

    this._clearRecentUndoUserIdTimeout = setTimeout(() => {
      this._recentUndoUserId = undefined;
      this._clearRecentUndoUserIdTimeout = undefined;
    }, ONE_SECOND_MS);
  }

  getUserById(id: number) {
    return this.users.find((user) => user.id === id);
  }

  setUsers(users: UserDetailed[]) {
    const usersById = new Map(this.users.map((user) => [user.id, user]));
    this.users = users.map((user) =>
      withStableAvatars(user, usersById.get(user.id))
    );
  }

  addOrUpdateUsers(users: UserDetailed[]) {
    const usersById = new Map(this.users.map((user) => [user.id, user]));
    const newUsers = users.map((user) =>
      withStableAvatars(user, usersById.get(user.id))
    );
    this.users = [...this.users, ...newUsers];
  }

  prependUser(user: UserDetailed) {
    const existingUser = this.users.find((u) => u.id === user.id);

    if (existingUser) {
      return;
    }

    this.users = [user, ...this.users];
  }

  removeByUser(userId: User['id']) {
    this.users = this.users.filter((storedUser) => storedUser.id !== userId);
  }

  setUserUndoInfo(undoInfo: UndoInfo | null) {
    this.undoInfo = undoInfo;
  }

  setFetchedAllUsers(fetched: boolean) {
    this.fetchedAllUsers = fetched;
  }

  setFiltersExtended(value: boolean) {
    this.filtersExtended = value;
  }

  setRestoredFromCache(value: boolean) {
    this.restoredFromCache = value;
  }
}

const discoveryStore = new DiscoveryStore();
export default discoveryStore;
